import React from "react";
import { Alert, Button, Collapse, Empty, Modal, Skeleton, Table, Tag, theme } from "antd";
import { CalculatorOutlined, CheckCircleFilled, CloseCircleFilled, EditOutlined, EyeOutlined,
  FileTextOutlined, InboxOutlined, InfoCircleOutlined } from "@ant-design/icons";
import apiClient from "../api/apiClient";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import useBusinessResource from "../hooks/useBusinessResource";
import "./service-details-modal.css";

const number = (value, digits = 2) => value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
  ? "—" : Number(value).toLocaleString("fr-FR", { maximumFractionDigits: digits });
const money = (value) => number(value) === "—" ? "—" : `${number(value)} CFA`;
const date = (value) => {
  if (!value) return "Non renseigné";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Non renseigné"
    : parsed.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};

export function useServiceDetailsStyle() {
  const { token } = theme.useToken();
  const dark = token.colorBgBase === "#000" || token.colorBgBase === "#000000";
  return {
    "--sd-text": token.colorText, "--sd-secondary": token.colorTextSecondary,
    "--sd-heading": dark ? token.colorTextHeading : token.colorInfoTextActive, "--sd-border": token.colorBorderSecondary,
    "--sd-surface": token.colorBgElevated, "--sd-fill": token.colorFillAlter,
    "--sd-accent-bg": token.colorInfoBg, "--sd-accent": dark ? token.colorTextHeading : token.colorInfoText,
    "--sd-success": dark ? token.colorSuccessText : token.colorSuccessTextActive,
    "--sd-error": dark ? token.colorErrorText : token.colorErrorTextActive,
  };
}

export function ServiceDetailsHeading({ service }) {
  return <div className="service-details__heading">
    <span className="service-details__eye"><EyeOutlined /></span>
    <div className="service-details__identity">
      <div className="service-details__title-row"><h2>Détails du service</h2>
        {service && <Tag color={Number(service.is_active) === 1 ? "success" : "default"}
          icon={Number(service.is_active) === 1 ? <CheckCircleFilled /> : <CloseCircleFilled />}>
          {Number(service.is_active) === 1 ? "Actif" : "Inactif"}
        </Tag>}
      </div>
      {service && <div className="service-details__subtitle"><strong>{service.name}</strong>
        <span>{service.code || "Sans code interne"}</span></div>}
    </div>
  </div>;
}

export function ServiceDetailsContent({ service, previewWarehouseName }) {
  const preview = service.cost_preview;
  const blockingReasons = preview?.blocking_reasons || [];
  const incomplete = !preview || (service.components?.length > 0 &&
    (!preview.warehouse_id || preview.components_breakdown?.length !== service.components.length
      || preview.components_breakdown.some((line) => line.unit_cost == null || line.line_cost == null)));
  const breakdown = new Map((preview?.components_breakdown || []).map((row) => [Number(row.product_id), row]));
  const components = (service.components || []).map((component) => ({
    ...component, costing: breakdown.get(Number(component.product_id)),
  }));
  const calculationWarehouse = service.warehouse_name || previewWarehouseName;
  const negativeMargin = !incomplete && Number(preview.pricing_gap) < 0;
  const columns = [
    { title: "Composant", dataIndex: "product_name", key: "name", render: (value) => value || "Produit indisponible" },
    { title: "Qté par service", dataIndex: "quantity_per_service", key: "quantity", align: "right", render: (value) => number(value, 4) },
    { title: "Perte", dataIndex: "wastage_percent", key: "wastage", align: "right", render: (value) => `${number(value)} %` },
    { title: "Coût unitaire", key: "unit_cost", align: "right", render: (_, row) => money(row.costing?.unit_cost) },
    { title: "Coût estimé", key: "line_cost", align: "right", render: (_, row) => money(row.costing?.line_cost) },
  ];
  return <div className="service-details__grid">
    <div className="service-details__main">
      <section className="service-details__section" aria-labelledby="service-details-general">
        <h3 id="service-details-general"><FileTextOutlined /> Informations générales</h3>
        <dl className="service-details__facts">
          <div><dt>Entreprise</dt><dd>{service.company_name || "Non renseignée"}</dd></div>
          <div><dt>Établissement</dt><dd>{service.warehouse_name || "Global entreprise"}</dd></div>
          <div><dt>Durée</dt><dd>{service.duration_minutes ? `${number(service.duration_minutes)} min` : "Non renseignée"}</dd></div>
          <div><dt>Produit associé</dt><dd>{service.product_id
            ? service.product_name || "Produit indisponible" : "Aucun — vente directe"}</dd></div>
        </dl>
        <div className="service-details__description"><span>Description</span>
          <p>{service.description || "Aucune description renseignée."}</p></div>
      </section>
      <section className="service-details__section" aria-labelledby="service-details-components">
        <h3 id="service-details-components"><InboxOutlined /> Composants consommables
          <span className="service-details__count">{components.length}</span></h3>
        <Table rowKey="product_id" size="small" pagination={false} columns={columns} dataSource={components}
          scroll={{ x: 550 }} locale={{ emptyText: "Aucun composant consommable pour ce service." }} />
      </section>
      <Collapse className="service-details__metadata" defaultActiveKey={["metadata"]}
        expandIconPosition="end" items={[{ key: "metadata",
          label: <span><InfoCircleOutlined /> Informations complémentaires
            <small className="service-details__reference">Identifiant : {service.id}</small></span>,
          children: <dl className="service-details__facts">
            <div><dt>Créé le</dt><dd>{date(service.created_at)}</dd></div>
            <div><dt>Mis à jour le</dt><dd>{date(service.updated_at)}</dd></div>
          </dl>,
        }]} />
    </div>
    <aside className="service-details__summary" aria-labelledby="service-details-summary">
      <h3 id="service-details-summary"><CalculatorOutlined /> Synthèse financière</h3>
      <p className="service-details__context">{calculationWarehouse
        ? `Calcul estimé pour ${calculationWarehouse}` : "Aucun établissement de calcul sélectionné"}</p>
      {(incomplete || blockingReasons.length > 0) && <Alert type="warning" showIcon
        message={incomplete ? "Estimation indisponible" : "Points à vérifier"}
        description={blockingReasons.length ? blockingReasons.join(" ") : "Les données de coût ne sont pas disponibles."} />}
      {negativeMargin && <Alert type="warning" showIcon message="Le tarif est inférieur au coût estimé." />}
      <dl className="service-details__totals">
        <div><dt>Tarif de base</dt><dd>{money(service.base_price)}</dd></div>
        <div><dt>Coût estimé</dt><dd>{money(incomplete ? null : preview.service_cost_total)}</dd></div>
        <div><dt>Marge estimée</dt><dd className={negativeMargin ? "service-details__negative" : "service-details__positive"}>
          {money(incomplete ? null : preview.pricing_gap)}</dd></div>
      </dl>
      <h4>Détail du coût estimé</h4>
      <dl className="service-details__costs">
        <div><dt>Consommables</dt><dd>{money(incomplete ? null : preview.consumables_cost_total)}</dd></div>
        <div><dt>Main d’œuvre</dt><dd>{money(preview?.labor_cost ?? service.labor_cost)}</dd></div>
        <div><dt>Charges fixes</dt><dd>{money(preview?.overhead_cost ?? service.overhead_cost)}</dd></div>
        <div className="service-details__cost-total"><dt>Total</dt><dd>{money(incomplete ? null : preview.service_cost_total)}</dd></div>
      </dl>
      <Alert className="service-details__estimate-note" type="info" showIcon
        message="Ces montants sont des estimations basées sur les composants définis pour ce service et les coûts de l’établissement de calcul." />
    </aside>
  </div>;
}

export default function ServiceDetailsModal({ open, serviceId, companyId, userId, warehouseId,
  previewWarehouseId, previewWarehouseName, canEdit, onClose, onEdit }) {
  const style = useServiceDetailsStyle();
  const resource = useBusinessResource({ resource: "service-detail", userId, companyId, warehouseId,
    moduleScope: "commercial", scopeLevel: "company", parameters: [serviceId, previewWarehouseId || null],
    enabled: Boolean(open && serviceId), active: open,
    load: async (signal) => {
      const response = await apiClient.get(`/api/services/${serviceId}`, { signal,
        params: { company_id: companyId, ...(previewWarehouseId ? { preview_warehouse_id: previewWarehouseId } : {}) },
      });
      return response.data;
    },
  });
  const accessLost = [401, 403, 404].includes(resource.error?.response?.status);
  const service = accessLost ? null : resource.data;
  const verified = resource.status === "verified" && !resource.error && !resource.loading;
  const errorMessage = resource.error?.response?.data?.error || "Impossible d’actualiser les informations du service.";
  return <Modal open={open} onCancel={onClose} centered width={1020} className="service-details-modal"
    style={style} title={<ServiceDetailsHeading service={service} />} destroyOnClose
    footer={<><Button onClick={onClose}>Fermer</Button>
      {canEdit && <Button type="primary" icon={<EditOutlined />} disabled={!service || !verified}
        onClick={() => { if (service && verified) onEdit(service); }}>Modifier le service</Button>}</>}>
    <BusinessResourceNotice resource={accessLost ? { ...resource, data: undefined } : resource}
      label="la fiche du service" errorMessage={errorMessage} />
    {!service && resource.loading ? <Skeleton active paragraph={{ rows: 9 }} />
      : service ? <ServiceDetailsContent service={service} previewWarehouseName={previewWarehouseName} />
        : !resource.error && <Empty description="Les informations du service ne sont pas disponibles." />}
  </Modal>;
}
